import { useState, useCallback } from "react";

const STORAGE_KEY = "greyrock_onboarding";

interface OnboardingState {
  firstAnalysisDone: boolean;
  tipsDismissed: string[];
  creditGuideShown: boolean;
}

const getState = (): OnboardingState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { firstAnalysisDone: false, tipsDismissed: [], creditGuideShown: false };
};

const saveState = (state: OnboardingState) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

export const useOnboarding = () => {
  const [state, setState] = useState<OnboardingState>(getState);

  const dismissTip = useCallback((tipId: string) => {
    setState((prev) => {
      const next = { ...prev, tipsDismissed: [...prev.tipsDismissed, tipId] };
      saveState(next);
      return next;
    });
  }, []);

  const markFirstAnalysis = useCallback(() => {
    setState((prev) => {
      const next = { ...prev, firstAnalysisDone: true };
      saveState(next);
      return next;
    });
  }, []);

  const markCreditGuideShown = useCallback(() => {
    setState((prev) => {
      const next = { ...prev, creditGuideShown: true };
      saveState(next);
      return next;
    });
  }, []);

  const isTipDismissed = useCallback(
    (tipId: string) => state.tipsDismissed.includes(tipId),
    [state.tipsDismissed]
  );

  const isNewUser = !state.firstAnalysisDone;

  return {
    isNewUser,
    isTipDismissed,
    dismissTip,
    markFirstAnalysis,
    creditGuideShown: state.creditGuideShown,
    markCreditGuideShown,
  };
};
